import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "tw:group/button! tw:inline-flex! tw:shrink-0! tw:items-center! tw:justify-center! tw:rounded-none! tw:border! tw:border-transparent! tw:bg-clip-padding! tw:text-xs! tw:font-medium tw:whitespace-nowrap! tw:transition-all! tw:outline-none tw:select-none! tw:focus-visible:border-ring! tw:focus-visible:ring-1 tw:focus-visible:ring-ring/50 tw:active:not-aria-[haspopup]:translate-y-px tw:disabled:pointer-events-none! tw:disabled:opacity-50! tw:aria-invalid:border-destructive! tw:aria-invalid:ring-1 tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:border-destructive/50! tw:dark:aria-invalid:ring-destructive/40! tw:[&_svg]:pointer-events-none! tw:[&_svg]:shrink-0! tw:[&_svg:not([class*='size-'])]:size-4!",
  {
    variants: {
      variant: {
        default: "tw:bg-primary! tw:text-primary-foreground! tw:[a]:hover:bg-primary/80!",
        outline:
          "tw:border-border! tw:bg-background! tw:hover:bg-muted! tw:hover:text-foreground! tw:aria-expanded:bg-muted! tw:aria-expanded:text-foreground! tw:dark:border-input! tw:dark:bg-input/30! tw:dark:hover:bg-input/50!",
        secondary:
          "tw:bg-secondary! tw:text-secondary-foreground! tw:hover:bg-secondary/80! tw:aria-expanded:bg-secondary! tw:aria-expanded:text-secondary-foreground!",
        ghost:
          "tw:hover:bg-muted! tw:hover:text-foreground! tw:aria-expanded:bg-muted! tw:aria-expanded:text-foreground! tw:dark:hover:bg-muted/50!",
        destructive:
          "tw:bg-destructive/10! tw:text-destructive! tw:hover:bg-destructive/20! tw:focus-visible:border-destructive/40! tw:focus-visible:ring-destructive/20 tw:dark:bg-destructive/20! tw:dark:hover:bg-destructive/30! tw:dark:focus-visible:ring-destructive/40!",
        link: "tw:text-primary! tw:underline-offset-4! tw:hover:underline!",
      },
      size: {
        default:
          "tw:h-8! tw:gap-1.5! tw:px-2.5! tw:has-data-[icon=inline-end]:pr-2! tw:has-data-[icon=inline-start]:pl-2!",
        xs: "tw:h-6! tw:gap-1! tw:rounded-none! tw:px-2! tw:text-xs! tw:has-data-[icon=inline-end]:pr-1.5! tw:has-data-[icon=inline-start]:pl-1.5! tw:[&_svg:not([class*='size-'])]:size-3!",
        sm: "tw:h-7! tw:gap-1! tw:rounded-none! tw:px-2.5! tw:has-data-[icon=inline-end]:pr-1.5! tw:has-data-[icon=inline-start]:pl-1.5! tw:[&_svg:not([class*='size-'])]:size-3.5!",
        lg: "tw:h-9! tw:gap-1.5! tw:px-2.5! tw:has-data-[icon=inline-end]:pr-2! tw:has-data-[icon=inline-start]:pl-2!",
        icon: "tw:size-8!",
        "icon-xs": "tw:size-6! tw:rounded-none! tw:[&_svg:not([class*='size-'])]:size-3!",
        "icon-sm": "tw:size-7! tw:rounded-none!",
        "icon-lg": "tw:size-9!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
